import { useQueryClient } from "@tanstack/react-query";
import { produce } from "immer";

import getQueryKeys from "common/datahooks/getQueryKeys";
import { useSelectedWorkspaceContext } from "common/helpers/SelectedWorkspaceContext";
import type { Campaign } from "common/types";

import type { DetailedCampaign } from "../types";

export default function useUpdateCampaign() {
  const queryClient = useQueryClient();
  const { id: workspaceId } = useSelectedWorkspaceContext();
  const { campaignsKeys } = getQueryKeys(workspaceId);

  function updateCachedCampaigns(
    updateCampaigns: (campaigns: Campaign[]) => Campaign[],
  ) {
    if (queryClient.getQueryData(campaignsKeys.list())) {
      queryClient.setQueryData(
        campaignsKeys.list(),
        (prevCampaigns: Campaign[]) =>
          produce(prevCampaigns, (draftState) => updateCampaigns(draftState)),
      );
    }
  }

  function updateCachedCampaign(
    campaignId: string,
    updateCampaign: (campaign: DetailedCampaign) => void,
  ) {
    if (queryClient.getQueryData(campaignsKeys.details(campaignId))) {
      queryClient.setQueryData(
        campaignsKeys.details(campaignId),
        (prevCampaign: DetailedCampaign) =>
          produce(prevCampaign, updateCampaign),
      );
    }
  }

  // Used for updating Campaign list when the single campaign changes
  function updateCachedCampaignInList(
    campaignId: string,
    updates: Partial<
      Pick<Campaign, "name" | "miner" | "state" | "start_date" | "owners">
    >,
  ) {
    if (queryClient.getQueryData(campaignsKeys.list())) {
      queryClient.setQueryData(
        campaignsKeys.list(),
        (prevCampaigns: DetailedCampaign[]) =>
          produce(prevCampaigns, (draftCampaigns) => {
            const campaignIndex = draftCampaigns.findIndex(
              (campaign) => campaign.id === campaignId,
            );

            draftCampaigns[campaignIndex] = {
              ...draftCampaigns[campaignIndex],
              ...updates,
            };
          }),
      );
    }
  }

  return {
    updateCachedCampaign,
    updateCachedCampaigns,
    updateCachedCampaignInList,
  };
}
